import { useId, useLayoutEffect, useRef, useSyncExternalStore, type ChangeEvent, type ReactNode, type Ref, type TextareaHTMLAttributes } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { X } from '@phosphor-icons/react';
import { fmtTime } from '../lib/format.ts';

/*
 * Bausteine im Arnold-Erscheinungsbild.
 *
 * Die Formen stammen eins zu eins aus dem Arnold AG Design System
 * (`design/arnold-design-system.css`, Komponenten in `design/ds_bundle.js`).
 * Die Vorlage liefert sie als React-Komponenten mit Inline-Stilen; hier stehen
 * dieselben Werte als Tailwind-Klassen, damit die Anwendung eine Sprache spricht.
 *
 * Drei Regeln aus dem Design System, die überall gelten:
 *   - scharfe Ecken; Radius nur bei `Tag` (Pille)
 *   - Beschriftungen in der Anzeigeschrift, versal und gesperrt
 *   - Umschalten darkt nach, es schrumpft nicht
 */

const badgeTones = {
  neutral: 'bg-steel-100 text-steel-800',
  red: 'bg-arnold-red text-white',
  steel: 'bg-steel-800 text-white',
  ok: 'bg-ok-tint text-ok',
  warn: 'bg-warn-tint text-warn',
  error: 'bg-error-tint text-error',
  info: 'bg-info-tint text-info',
} as const;

export type BadgeTone = keyof typeof badgeTones;

export function Badge({
  tone = 'neutral',
  children,
  title,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`ad-badge ${badgeTones[tone]}`} title={title}>
      {children}
    </span>
  );
}

const buttonVariants = {
  primary: 'ad-button-primary',
  secondary: 'ad-button-secondary',
  outline: 'ad-button-outline',
  ghost: 'ad-button-ghost',
} as const;

const buttonSizes = {
  sm: 'ad-button-sm',
  md: '',
  lg: 'ad-button-lg',
} as const;

export function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled,
  onClick,
  className = '',
  children,
}: {
  variant?: keyof typeof buttonVariants;
  size?: keyof typeof buttonSizes;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`${buttonVariants[variant]} ${buttonSizes[size]} ${className}`}
    >
      {children}
    </button>
  );
}

/** Filterschalter. Aktiv heißt gefüllt — der Zustand muss ohne Hinsehen lesbar sein. */
export function Tag({
  active = false,
  onClick,
  children,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`ad-tag ${active ? 'ad-tag-active' : ''}`}
    >
      {children}
    </button>
  );
}

export function Card({
  kicker,
  title,
  footer,
  onClick,
  className = '',
  children,
}: {
  kicker?: ReactNode;
  title?: ReactNode;
  footer?: ReactNode;
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
}) {
  const clickable = Boolean(onClick);
  const body = (
    <>
      {kicker ? <div className="mb-1 text-[14px] font-semibold text-arnold-red">{kicker}</div> : null}
      {title ? <h2 className="mb-3 text-[20px]">{title}</h2> : null}
      {children}
      {footer ? (
        <div className="-mx-5 -mb-5 mt-5 border-t border-[var(--border-default)] bg-steel-50 px-5 py-3.5">
          {footer}
        </div>
      ) : null}
    </>
  );

  if (!clickable) return <div className={`ad-card ${className}`}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ad-card ad-card-clickable w-full text-left ${className}`}
    >
      {body}
    </button>
  );
}

/** Beschriftung, Hinweis und Fehler um ein Eingabefeld — wie `Field` im Design System. */
export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      {label ? (
        <span className="ad-label">
          {label}
          {required ? <span className="text-arnold-red"> *</span> : null}
        </span>
      ) : null}
      {children}
      {error ? (
        <span className="mt-1.5 block text-[14px] leading-snug font-medium text-error">{error}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-[14px] leading-snug text-[var(--text-muted)]">{hint}</span>
      ) : null}
    </label>
  );
}

export function Input({
  label,
  hint,
  error,
  required,
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled,
  inputMode,
  maxLength,
  autoComplete,
  autoFocus,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel' | 'url' | 'search';
  maxLength?: number;
  autoComplete?: string;
  autoFocus?: boolean;
}) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <input
        className="ad-input"
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={inputMode}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        data-autofocus={autoFocus ? '' : undefined}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function Select({
  label,
  hint,
  error,
  required,
  value,
  onChange,
  options,
  disabled,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <select
        className="ad-select"
        value={value}
        disabled={disabled}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`inline-flex min-h-9 items-center gap-2.5 text-[16px] select-none ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
        className="h-5 w-5 shrink-0 accent-[var(--color-arnold-red)]"
      />
      {label}
    </label>
  );
}

/** Reiter mit rotem Unterstrich am aktiven Eintrag. `inverse` für dunklen Grund. */
export function Tabs({
  items,
  active,
  onChange,
  inverse = false,
}: {
  items: { id: string; label: string; count?: number | undefined }[];
  active: string;
  onChange: (id: string) => void;
  inverse?: boolean;
}) {
  return (
    <div
      role="tablist"
      className={`flex gap-6 border-b ${inverse ? 'border-steel-700' : 'border-[var(--border-default)]'}`}
    >
      {items.map((it) => {
        const on = it.id === active;
        const color = inverse
          ? on
            ? 'text-white'
            : 'text-steel-400 hover:text-steel-200'
          : on
            ? 'text-steel-900'
            : 'text-steel-600 hover:text-steel-800';
        return (
          <button
            key={it.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(it.id)}
            className={`-mb-px inline-flex min-h-11 shrink-0 items-center gap-2 border-b-[3px] px-0.5 text-[16px] font-semibold whitespace-nowrap transition-colors ${color} ${
              on ? 'border-arnold-red' : 'border-transparent'
            }`}
          >
            {it.label}
            {it.count !== undefined ? (
              <span className={`min-w-6 px-1.5 text-center font-mono text-[13px] leading-5 font-medium ${on ? 'bg-steel-900 text-white' : 'bg-steel-100 text-steel-700'}`}>{it.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Ohne Netz hält TanStack Query Abfragen an, `isPending` bleibt dann bis zur Netzrückkehr wahr.
 * Ein Platzhalter sagte, es lade noch. Die Abfragen laufen danach von selbst weiter, darum stimmt der Hinweis.
 */
const useQueriesOnline = () => useSyncExternalStore(onlineManager.subscribe, () => onlineManager.isOnline());

function OfflineNote() {
  return <Notice tone="warn">Kein Netz. Die Daten erscheinen, sobald die Verbindung wieder steht.</Notice>;
}

/**
 * Ladezustand: Platzhalterbalken in der ungefähren Form von Text statt eines
 * Drehkreisels – die Seite springt beim Laden weniger. Der Text bleibt für
 * Screenreader erhalten.
 */
export function Spinner({ label = 'Wird geladen …', lines = 3 }: { label?: string; lines?: number }) {
  const online = useQueriesOnline();
  return (
    <div className="space-y-3 py-6" role="status" aria-live="polite">
      {online ? (
        <>
          <span className="sr-only">{label}</span>
          {Array.from({ length: lines }, (_, i) => (
            <div key={i} className="ad-skeleton h-4" style={{ width: `${[72, 92, 56, 84, 64][i % 5]}%` }} aria-hidden />
          ))}
        </>
      ) : (
        <OfflineNote />
      )}
    </div>
  );
}

/** Platzhalter in Form einer Liste von Karten (Ticketliste, Maschinen). */
export function ListSkeleton({ rows = 5, label = 'Wird geladen …' }: { rows?: number; label?: string }) {
  const online = useQueriesOnline();
  return (
    <div className="grid gap-2" role="status" aria-live="polite">
      {online ? (
        <>
          <span className="sr-only">{label}</span>
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="border border-[var(--border-default)] bg-white p-4" aria-hidden>
              <div className="flex gap-2">
                <div className="ad-skeleton h-5 w-20" />
                <div className="ad-skeleton h-5 w-12" />
                <div className="ad-skeleton h-5 w-24" />
              </div>
              <div className="ad-skeleton mt-3 h-5" style={{ width: `${[64, 48, 72, 56, 68][i % 5]}%` }} />
              <div className="ad-skeleton mt-2 h-4 w-2/5" />
            </div>
          ))}
        </>
      ) : (
        <OfflineNote />
      )}
    </div>
  );
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unerwarteter Fehler. Bitte erneut versuchen.';
}

export function ErrorNote({ error }: { error: unknown }) {
  return (
    <div
      role="alert"
      className="border-l-4 border-error bg-error-tint px-4 py-3 text-[15px] leading-snug whitespace-pre-line text-error"
    >
      {errorMessage(error)}
    </div>
  );
}

/** Leerer Zustand: sagt, was hier sonst steht und wie es dahin kommt. */
export function EmptyState({ title, hint, icon, action }: { title: string; hint?: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center border border-dashed border-steel-300 bg-white px-6 py-10 text-center">
      {icon ? <div className="mb-3 text-steel-400">{icon}</div> : null}
      <p className="font-display text-[19px] font-bold text-steel-900">{title}</p>
      {hint ? <p className="mt-1.5 max-w-[52ch] text-[15px] text-[var(--text-muted)]">{hint}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function PageTitle({
  children,
  lead,
  action,
}: {
  children: ReactNode;
  lead?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-[clamp(24px,3.2vw,30px)]">{children}</h1>
        {lead ? <p className="mt-1.5 max-w-[70ch] text-[16px] text-[var(--text-muted)]">{lead}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Textarea({
  label,
  hint,
  error,
  required,
  value,
  onChange,
  rows = 4,
  placeholder,
  maxLength,
  autoFocus,
  inputRef,
  ...assist
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  /** Für Eingabehilfen, die die Cursorposition brauchen (@-Erwähnung). */
  inputRef?: Ref<HTMLTextAreaElement>;
} & Pick<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onKeyDown' | 'onSelect' | 'onBlur' | 'aria-autocomplete' | 'aria-controls' | 'aria-activedescendant'>) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <textarea
        {...assist}
        ref={inputRef}
        className="ad-input min-h-[88px] resize-y"
        rows={rows}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        autoFocus={autoFocus}
        data-autofocus={autoFocus ? '' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

type ModalProps = {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Fehler beim Absenden. Steht im klebenden Fuß, im scrollenden Körper läge er außer Sicht oder hinter der Fußleiste. */
  error?: unknown;
  /** Für Inhalte, die Breite brauchen (PDF-Vorschau). */
  wide?: boolean;
  /** Nur für Ansichten ohne Eingaben, etwa Foto oder PDF. */
  closeOnBackdrop?: boolean;
};

/**
 * Dialog über der Seite. Schließt mit ✕, Escape oder den eigenen Knöpfen; auf
 * dem Handy füllt er die Breite (Handschuhe, große Ziele). Ein Klick auf den
 * Hintergrund schließt nur reine Ansichten (`closeOnBackdrop`): In Formularen
 * gingen sonst Eingaben verloren, schon wenn man beim Markieren von Text die
 * Maus erst außerhalb loslässt.
 */
export function Modal({ open, ...dialog }: ModalProps & { open: boolean }) {
  return open ? <ModalDialog {...dialog} /> : null;
}

/** Ist der Auslöser weg, stünde der Fokus auf `body`. Die Überschrift gibt Tastatur und Screenreader Halt, ohne die Seite zu verschieben. */
function focusHeadingIfLost() {
  if (document.activeElement !== document.body) return;
  const heading = document.querySelector('h1');
  if (!heading) return;
  heading.tabIndex = -1;
  heading.focus({ preventScroll: true });
}

/**
 * Natives `<dialog>`: `showModal()` bringt oberste Ebene, Fokusfalle und inerte Seite,
 * `close()` gibt den Fokus an den Auslöser zurück. Geöffnet und geschlossen wird nur
 * über das Mounten, den Zustand führt React. `data-modal-panel` markiert den Scrollbereich.
 */
function ModalDialog({ title, onClose, children, footer, error, wide = false, closeOnBackdrop = false }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // Schließen nur, wenn der Klick auch auf dem Hintergrund begonnen hat.
  const pressedOnBackdrop = useRef(false);

  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    dialog.showModal();
    // Reacts `autoFocus` trifft den noch verborgenen Dialog und bleibt wirkungslos.
    const target = dialog.querySelector<HTMLElement>('[data-autofocus]') ?? dialog.querySelector<HTMLElement>('[data-modal-panel]');
    target?.focus();
    return () => {
      dialog.close();
      // Erst nach dem Commit prüfen: Der Auslöser kann im selben Zug verschwinden. Später prüft niemand mehr,
      // darum schließt ein Dialog, dessen Absenden den Auslöser wegnimmt, erst nach dem Neuladen.
      queueMicrotask(focusHeadingIfLost);
    };
  }, []);

  // `open:flex` statt `flex`: Vor `showModal()` muss der Dialog verborgen bleiben. Sonst greift `autoFocus`,
  // und der Browser merkt sich das Eingabefeld statt des Auslösers.
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className="fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none items-end justify-center bg-[rgba(20,25,29,0.6)] text-inherit backdrop:bg-transparent open:flex sm:items-center"
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (closeOnBackdrop && pressedOnBackdrop.current && e.target === e.currentTarget) onClose();
      }}
      onCancel={(e) => {
        // Auch das `cancel` einer Dateiauswahl oder eines inneren Dialogs kommt hier an.
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        onClose();
      }}
      onClose={(e) => {
        // Der Browser darf `cancel` überspringen und selbst schließen. Das `close` aus dem
        // Doppellauf von StrictMode trifft dagegen einen wieder offenen Dialog.
        if (e.target === e.currentTarget && !ref.current?.open) onClose();
      }}
    >
      <div
        data-modal-panel
        tabIndex={-1}
        className={`max-h-[92dvh] w-full ${wide ? 'max-w-[1100px]' : 'max-w-[560px]'} overflow-auto bg-white shadow-[var(--shadow-overlay)]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[var(--border-default)] px-5 py-3">
          <h2 id={titleId} className="text-[21px]">{title}</h2>
          <button
            type="button"
            aria-label="Schließen"
            className="-mr-2 inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center text-steel-600 hover:bg-steel-100 hover:text-steel-900"
            onClick={onClose}
          >
            <X size={22} />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">{children}</div>
        {footer || error ? (
          <div className="sticky bottom-0 space-y-3 border-t border-[var(--border-default)] bg-steel-50 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {/* Eine lange Meldung scrollt für sich, damit die Knöpfe erreichbar bleiben. */}
            {error ? (
              <div className="max-h-[40dvh] overflow-auto">
                <ErrorNote error={error} />
              </div>
            ) : null}
            {footer ? <div className="flex flex-wrap justify-end gap-2">{footer}</div> : null}
          </div>
        ) : null}
      </div>
    </dialog>
  );
}

/** Kurzer Hinweis (Erfolg/Info) – verschwindet nicht von selbst, damit ihn jeder liest. */
export function Notice({ tone = 'info', children }: { tone?: 'info' | 'ok' | 'warn'; children: ReactNode }) {
  const tones = {
    info: 'border-info bg-info-tint text-info',
    ok: 'border-ok bg-ok-tint text-ok',
    warn: 'border-warn bg-warn-tint text-warn',
  } as const;
  return <div className={`border-l-4 px-4 py-3 text-[15px] leading-snug ${tones[tone]}`}>{children}</div>;
}

/**
 * Meldung unter dem Knopf, der sie ausgelöst hat. Die Karte wächst dabei nach unten: Am Seitenende läge die
 * Meldung sonst unter dem Bildschirmrand oder der Navigation. Ändert sich `revealOn`, rollt sie erneut ins Bild.
 * Der Wert gehört der eigenen Aktion: Abfragedaten ändern sich auch durch andere Aktionen und zögen die Seite weg.
 */
export function ActionNote({ revealOn, className = '', children }: { revealOn?: unknown; className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest' });
  }, [revealOn]);
  return (
    <div ref={ref} className={`scroll-mb-[calc(6rem+env(safe-area-inset-bottom))] md:scroll-mb-4 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Ein gescheiterter Hintergrund-Abruf lässt die Seite stehen, statt sie zu ersetzen: Der Hinweis sagt,
 * wie alt die Anzeige ist. `paused` heißt, der Browser meldet sich offline und der Abruf läuft gar nicht los.
 */
export function StaleNote({ query }: { query: { isRefetchError: boolean; fetchStatus: string; dataUpdatedAt: number } }) {
  if (!query.isRefetchError && query.fetchStatus !== 'paused') return null;
  return <Notice tone="warn">Stand von {fmtTime(new Date(query.dataUpdatedAt).toISOString())}. Die Anzeige konnte nicht aktualisiert werden.</Notice>;
}
